import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export type CrmBranding = {
  enabled: boolean;
  name: string;
  logo: string | null;
  favicon: string | null;
  primaryColor: string;
  secondaryColor: string;
};
export type CrmContext = { tenantId: string | null; branding: CrmBranding | null };
export type CrmWhiteLabelSettings = {
  branding: CrmBranding;
  domain: string;
};

/** Resolves hostname branding before mounting the CRM, without sending stored credentials. */
export function useCrmContext() {
  return useQuery({ queryKey: ["crm-context", window.location.host],
    queryFn: async (): Promise<CrmContext> => {
      const response = await fetch("/api/crm/context", { credentials: "omit", cache: "no-store" });
      if (!response.ok) throw new Error("This domain is not connected to an active CRM workspace.");
      return response.json() as Promise<CrmContext>;
    }, retry: false, refetchOnWindowFocus: true, staleTime: 0 });
}

/** Keeps platform-only white-label configuration isolated by the selected tenant ID. */
export function useCrmWhiteLabelSettings(tenantId: string) {
  const client = useQueryClient();
  const queryKey = ["admin-crm-white-label", tenantId];
  const url = `/api/admin/tenants/${tenantId}/crm-white-label`;
  const query = useQuery({ queryKey, queryFn: async (): Promise<CrmWhiteLabelSettings> => (await axios.get(url)).data });
  const save = useMutation({ mutationFn: async (settings: Pick<CrmWhiteLabelSettings, "domain" | "branding">): Promise<CrmWhiteLabelSettings> =>
    (await axios.put(url, settings)).data, onSuccess: (settings) => client.setQueryData(queryKey, settings) });
  return { query, save };
}
